import { FeaColorConfiguration } from './fea-color-configuration';
import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';
import { Util } from '@phdsoft/common';
import * as THREE from '@phdsoft/three';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { Region } from 'src/app/platform/busines-data/Framework/Region';
import { SurfaceElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/SurfaceElement';
import { BufferGeometry } from '@phdsoft/three';
import { FEAElement } from '../models/fea-element';
import FeaUtils from '../FeaUtils';

export class FeaResult {
    public ElementsByRegion: {};
    private byNode: boolean;
    private isSmooth: boolean;
    private colorConfig: FeaColorConfiguration;
    private feaVector: Record<number, number> = null;
    private meshLines: THREE.LineSegments[] | null = null;
    private showMesh: boolean = false;

    constructor(
        private asset: Asset, byNode: boolean, isSmooth: boolean, colorConfig: FeaColorConfiguration = null
    ) {
        this.ElementsByRegion = {};
        this.byNode = byNode;
        this.isSmooth = isSmooth;
        this.colorConfig = colorConfig;
    }

    public setColorConfiguration(colorConfig: FeaColorConfiguration): void {
      this.colorConfig = colorConfig
    }

    public populateFeaResult(feaVector: {}): void {
      // Retain the raw vector for the merged-mesh coloring pass (renderToLightSection).
      this.feaVector = feaVector as Record<number, number>;

      for (const region of this.asset.GetStructureRegions()) { // TODO: when to optimize to just visible regions?
          if (!(region.Guid in this.ElementsByRegion)) {
              this.ElementsByRegion[region.Guid] = new Array<FEAElement>();
          }

          for (const element of region.SurfaceElements) {
              if (Util.isNullOrUndefined(element.femElementIds)) {
                  continue;
              }

              const feaElement = new FEAElement(element as SurfaceElement, feaVector, this.byNode);
              this.ElementsByRegion[region.Guid].push(feaElement);
          }

          for (const element of region.LinearElements) {
              if (Util.isNullOrUndefined(element.femElementIds)) {
                  continue;
              }

              const feaElement = new FEAElement(element as SurfaceElement, feaVector, this.byNode);
              this.ElementsByRegion[region.Guid].push(feaElement);
          }
      }
    }

    public getMinMaxByRegionAndVisibility(): {} {
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        for (let regionId in this.ElementsByRegion) {
          const regionElements = this.ElementsByRegion[regionId];
          for (const element of regionElements) {
            if (!this.isElementInScope(element)) {
                continue;
            }
            if (element.feaValue.length > 0) {
                min = Math.min(min, element.min);
                max = Math.max(max, element.max);
            }
          }
        }
        return { min: min, max: max };
    }

    public getSelectionAggregateData(selectedElementId: string, regionId: string): [string, number][] {
      if (!(regionId in this.ElementsByRegion)) {
        return null;
      }
      else {
        var element = this.ElementsByRegion[regionId].find(e => e.guid === selectedElementId) as FEAElement;
        if (Util.isNullOrUndefined(element)){
          return null;
        }
        if (element.min == element.max) {
          return [['Val', FeaUtils.formatFeaNumber(element.max)]] // could be average, but then we risk losing some precision
        }
        return [
          ['Max', FeaUtils.formatFeaNumber(element.max)],
          ['Avg', FeaUtils.formatFeaNumber(element.avg)],
          ['Min', FeaUtils.formatFeaNumber(element.min)]
        ]
      }
    }

    /**
     * Paint the FEA result onto the merged LightSection meshes via the baked
     * per-node/per-face FEA attributes (new-format GLBs). No-ops per mesh on
     * old-format GLBs, which lack the merged meshes and baked attributes. The
     * data model (populateFeaResult / min-max / selection) is unchanged and
     * still reads from the detailed elements.
     */
    public renderToLightSection(graphicsMaterials: GraphicsMaterials, regions: Array<Region>): void {
      if (!this.feaVector || !this.colorConfig) {
        return;
      }
      graphicsMaterials.applyFeaColorsToLightSection(
        regions,
        this.feaVector,
        this.colorConfig,
        { byNode: this.byNode }
      );
    }

    /**
     * Paint the FEA result on BOTH representations:
     *  - merged LightSection meshes (MODEL/REGIONS modes, new-format GLBs) via
     *    baked FEA attributes; no-ops per mesh on old-format GLBs;
     *  - detailed per-element meshes (SECTIONS navigation mode shows them via
     *    setViewOnSectionMode; old-format GLBs only have these).
     * Painting both keeps colors correct across navigation-mode switches
     * without re-triggering, at the cost of one extra pass at activation.
     */
    public render(graphicsMaterials: GraphicsMaterials, regions: Array<Region>): void {
      this.renderToLightSection(graphicsMaterials, regions);
      this.setFEAValueColorsByRegionVisibility();
    }

    public setFEAValueColorsByRegionVisibility(): void {
      for (let regionId in this.ElementsByRegion) {
        const regionElements = this.ElementsByRegion[regionId];
        for (const element of regionElements) {
          this.setElementColor(element)
        }
      }
    }

    /**
     * User-level visibility: the owning section (and via the group chain, its
     * region) is shown. Used ONLY to scope the LEGEND min/max
     * (getMinMaxByRegionAndVisibility) to user-visible sections — painting is
     * unconditional on both passes (colors on hidden meshes are harmless since
     * they are not rendered, and filtering caused reveal-without-repaint bugs).
     * Deliberately independent of WHICH representation (light vs detailed
     * groups) is currently rendered and of per-frame occlusion culling.
     */
    private isElementInScope(element: FEAElement): boolean {
      return !!element.baseElement?.section?.visible;
    }

    private setElementColor(element: FEAElement) {
      const elementGeometry = element.surfaceDesign.geometry as BufferGeometry;
      element.surfaceDesign.material = GraphicsMaterials.femMaterial;
      element.baseElement.currentMaterial = GraphicsMaterials.femMaterial;

      this.ensureColorAttribute(elementGeometry);

      if (this.byNode) {
        this.setColorByNode(element, elementGeometry);
      } else {
        if (this.isSmooth) {
          this.setSmoothColorByElement(element, elementGeometry);
        } else {
          this.setColorByElement(element, elementGeometry);
        }
      }

      const colorAttribute = elementGeometry.getAttribute('color');
      if (colorAttribute instanceof THREE.BufferAttribute) {
        colorAttribute.needsUpdate = true;
      }
    }

    private ensureColorAttribute(elementGeometry: BufferGeometry): void {
      const positionCount = elementGeometry.attributes.position.count;
      const colorAttribute = elementGeometry.getAttribute('color');

      if (colorAttribute instanceof THREE.BufferAttribute && colorAttribute.count === positionCount) {
        return;
      }

      elementGeometry.setAttribute(
        'color',
        new THREE.BufferAttribute(new Float32Array(positionCount * 3), 3),
      );
    }

    private setColorByNode(element: FEAElement, elementGeometry: BufferGeometry) {
      let nodeIndex = 0;

      for (const value of element.feaValue) {
        const colorValue = new THREE.Color(this.colorConfig.getColor(value));
        elementGeometry.attributes.color.setXYZ(nodeIndex++, colorValue.r, colorValue.g, colorValue.b);
      }
    }

    private setColorByElement(element: FEAElement, elementGeometry: BufferGeometry) {
      let nodeIndex = 0;
      let elementIndex = 0;

      for (const value of element.feaValue) {
        const colorValue = new THREE.Color(this.colorConfig.getColor(value));
        const topology = element.surfaceDesign.userData.ElementTopology[elementIndex++];

        switch (topology) {
          case 4: // Quadrangular element (6 nodes)
            for (let k = 0; k < 6; k++) {
              elementGeometry.attributes.color.setXYZ(nodeIndex++, colorValue.r, colorValue.g, colorValue.b);
            }
            break;
          case 2: // Triangular element (3 nodes)
            for (let k = 0; k < 3; k++) {
              elementGeometry.attributes.color.setXYZ(nodeIndex++, colorValue.r, colorValue.g, colorValue.b);
            }
            break;
          case 0: // Line element (2 nodes)
            const ringSize = this.getTubeRingSize(elementGeometry); // (radialSegments + 1)
            const vertsToPaint = ringSize * 2;                 // two rings per element
          
            for (let k = 0; k < vertsToPaint; k++) {
              elementGeometry.attributes.color.setXYZ(
                nodeIndex++,
                colorValue.r, colorValue.g, colorValue.b
              );
            }
            break;
        }
      }
    }

    private setSmoothColorByElement(element: FEAElement, elementGeometry: BufferGeometry) {
      const nodeValues: { [key: number]: number } = {};
      const nodeCounts: { [key: number]: number } = {};
      let nodeIndex = 0;
      let elementIndex = 0;

      // Step 1: Accumulate FEA values and counts for each node
      for (const value of element.feaValue) {
        const topology = element.surfaceDesign.userData.ElementTopology[elementIndex++];
        let nodes: number[] = [];

        switch (topology) {
          case 4: // Quadrangular element (6 nodes)
            nodes = [
              element.surfaceDesign.userData.NodesIds[nodeIndex],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 1],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 2],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 3],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 4],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 5]
            ];
            nodeIndex += 6;
            break;
          case 2: // Triangular element (3 nodes)
            nodes = [
              element.surfaceDesign.userData.NodesIds[nodeIndex],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 1],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 2]
            ];
            nodeIndex += 3;
            break;
          case 0: // Line element (2 nodes)
            nodes = [
              element.surfaceDesign.userData.NodesIds[nodeIndex],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 1]
            ];
            nodeIndex += 2;
            break;
        }

        if (Number.isNaN(value)) {
          continue; // Skip NaN values but let the counters nodeIndex elementIndex update before
        }

        for (const node of nodes) {
          if (!nodeValues[node]) {
            nodeValues[node] = 0;
            nodeCounts[node] = 0;
          }
          nodeValues[node] = Math.max(value, nodeValues[node]);
          //nodeValues[node] += value;
          //nodeCounts[node]++;
        }
      }

      // Step 2: Calculate average FEA value for each node
      // for (const node in nodeValues) {
      //   nodeValues[node] /= nodeCounts[node];
      // }

      // Step 3: Set colors for geometry based on averaged FEA values
      nodeIndex = 0;
      elementIndex = 0;
      for (const value of element.feaValue) {
        const topology = element.surfaceDesign.userData.ElementTopology[elementIndex++];
        let nodes: number[] = [];

        switch (topology) {
          case 4: // Quadrangular element (6 nodes)
            nodes = [
              element.surfaceDesign.userData.NodesIds[nodeIndex],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 1],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 2],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 3],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 4],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 5]
            ];
            break;
          case 2: // Triangular element (3 nodes)
            nodes = [
              element.surfaceDesign.userData.NodesIds[nodeIndex],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 1],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 2]
            ];
            break;
          case 0: // Line element (2 nodes)
            nodes = [
              element.surfaceDesign.userData.NodesIds[nodeIndex],
              element.surfaceDesign.userData.NodesIds[nodeIndex + 1]
            ];
            break;
        }

        for (const node of nodes) {
          const avgValue = !nodeValues[node] ? NaN : nodeValues[node];
          const color = new THREE.Color(this.colorConfig.getColor(avgValue));
          elementGeometry.attributes.color.setXYZ(nodeIndex++, color.r, color.g, color.b);
        }
      }
    }

    private getTubeRingSize(geom: THREE.BufferGeometry): number {
      // Prefer the parameter if present
      const p: any = (geom as any).parameters;
      if (p?.radialSegments !== undefined) return (p.radialSegments as number) + 1;
    
      // Fallback: infer from UVs (U is constant within a ring)
      const uv = geom.getAttribute('uv') as THREE.BufferAttribute | undefined;
      if (uv && uv.itemSize >= 2 && uv.count > 1) {
        const u0 = uv.getX(0);
        let count = 1;
        const eps = 1e-6;
        for (let i = 1; i < uv.count; i++) {
          if (Math.abs(uv.getX(i) - u0) < eps) count++;
          else break;
        }
        return count; // = radialSegments + 1
      }
    
      // Last resort: assume a common default of 8 radial segments
      return 9; // 8 + 1
    }

    /**
     * Set the mesh lines for this FEA result
     * @param lines Array of LineSegments representing the mesh
     */
    public setMeshLines(lines: THREE.LineSegments[]): void {
      this.meshLines = lines;

      if (this.meshLines) {
        this.meshLines.forEach(line => line.visible = this.showMesh);
      }
    }

    public getMeshLines(): THREE.LineSegments[] | null {
      return this.meshLines;
    }

    public setShowMesh(show: boolean): void {
      this.showMesh = show;
      if (this.meshLines) {
        this.meshLines.forEach(line => line.visible = show);
      }
    }

    public getShowMesh(): boolean {
      return this.showMesh;
    }

    public disposeMeshLines(): void {
      if (this.meshLines) {
        for (const lineSegment of this.meshLines) {
          if (lineSegment.geometry) {
            lineSegment.geometry.dispose();
          }
          if (lineSegment.parent) {
            lineSegment.parent.remove(lineSegment);
          }
        }
        this.meshLines = null;
      }
      this.showMesh = false;
    }
}
